/**
 * Keyboard input collector — DOM → InputState bridge.
 *
 * Stage 1a. Tracks A/D held state and a Space *edge* (kick true for exactly
 * one sim step per physical keydown). Uses `event.code` (layout-independent,
 * physical key position) so the game plays the same on AZERTY/DVORAK.
 *
 * Edge contract: `sampleInput()` returns the current held state plus a `kick`
 * flag that is true ONLY on the first sample after a Space keydown. The caller
 * must call `clearKickEdge()` once it has consumed the kick for that sim step,
 * so a held Space does not re-fire across steps. (Alternatively the caller may
 * call `sampleInput()` then `clearKickEdge()` immediately and feed the snapshot
 * to the sim — either order yields exactly one kick edge per press.)
 *
 * Pause toggling (Space/KeyP) is NOT handled here — it belongs to the game
 * state machine (Stage 1c), not the input collector.
 */

import type { InputState } from "../sim/types";

/**
 * Mutable input collector. Attach once; sample each frame.
 */
export class InputCollector {
  private left = false;
  private right = false;
  /** Space edge: set on keydown, cleared by `clearKickEdge`. */
  private kickEdge = false;

  private readonly onKeyDown: (e: KeyboardEvent) => void;
  private readonly onKeyUp: (e: KeyboardEvent) => void;
  private readonly onBlur: () => void;

  constructor(target: HTMLElement | Window = window) {
    this.onKeyDown = (e: KeyboardEvent): void => {
      switch (e.code) {
        case "KeyA":
        case "ArrowLeft":
          this.left = true;
          break;
        case "KeyD":
        case "ArrowRight":
          this.right = true;
          break;
        case "Space":
          // Edge: only arm on the initial keydown, not auto-repeat.
          if (!e.repeat) this.kickEdge = true;
          // Prevent page scroll on Space.
          e.preventDefault();
          break;
        default:
          return;
      }
    };

    this.onKeyUp = (e: KeyboardEvent): void => {
      switch (e.code) {
        case "KeyA":
        case "ArrowLeft":
          this.left = false;
          break;
        case "KeyD":
        case "ArrowRight":
          this.right = false;
          break;
        default:
          return;
      }
    };

    // Release everything when the window loses focus so the rider doesn't
    // strafe forever after alt-tabbing away.
    this.onBlur = (): void => {
      this.left = false;
      this.right = false;
      this.kickEdge = false;
    };

    target.addEventListener("keydown", this.onKeyDown as EventListener);
    target.addEventListener("keyup", this.onKeyUp as EventListener);
    window.addEventListener("blur", this.onBlur);
  }

  /**
   * Snapshot the current input. The `kick` field is true only until the next
   * `clearKickEdge()` call — consume it within one sim step.
   */
  sampleInput(): InputState {
    return { left: this.left, right: this.right, kick: this.kickEdge };
  }

  /**
   * Clear the kick edge so a held Space fires exactly once per press.
   * Call this after the sim has consumed the kick for the current step.
   */
  clearKickEdge(): void {
    this.kickEdge = false;
  }

  /**
   * Convenience: sample + clear in one call. Returns an InputState snapshot
   * with the kick edge armed for this step only.
   */
  sampleAndClear(): InputState {
    const input = this.sampleInput();
    this.clearKickEdge();
    return input;
  }

  /** Tear down DOM listeners. */
  dispose(): void {
    window.removeEventListener("blur", this.onBlur);
    window.removeEventListener("keydown", this.onKeyDown as EventListener);
    window.removeEventListener("keyup", this.onKeyUp as EventListener);
  }
}
